import React, { useEffect, useState } from 'react';
import { Flex, Menu, MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import BackArrow from '../../assets/images/back-arrow.svg';
import { MenuItemData } from '@/apis/types';
import theme from '@/assets/theme';

interface MenuState {
  items: MenuItemData[];
  status: string;
}

interface RootState {
  menu: MenuState;
}

// TODO(tim): use proper antd Menu types, rather than our own
interface MenuChild {
  key: string;
  type: 'item';
  label: string;
}

interface MenuGroup {
  key: string;
  type: 'group';
  label: string;
  children: MenuChild[];
}

interface SettingsSidebarProps {
  isUpdateForm?: boolean;
  backUrl?: string;
}

/**
 * Settings sidebar component with menu navigation
 *
 * @param isUpdateForm - Whether in update form mode (shows back button)
 * @param backUrl - URL to navigate back to
 */
const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  isUpdateForm,
  backUrl,
}) => {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');

  // menu from redux/store
  const main_menu = useSelector((state: RootState) => state.menu.items);
  const sub_menu = main_menu?.find((item) => item?.name === 'Settings');

  const items: MenuGroup[] = [];

  const subMenus = sub_menu?.sub_menus || [];

  const mySettingsChildren: MenuChild[] = [];
  const companySettingsChildren: MenuChild[] = [];
  const modulesChildren: MenuChild[] = [];

  // Helper function to translate menu labels
  const getTranslatedLabel = (itemLabel: string): string => {
    const normalizedLabel = itemLabel?.toLowerCase();

    // Theme-specific overrides
    if (normalizedLabel === 'teams' && theme.themeType === 2) {
      return tCommon('clients');
    }
    if (normalizedLabel === 'transport_providers' && theme.themeType === 2) {
      return tCommon('carriers');
    }

    // Translation map
    const labelMap: Record<string, string> = {
      general: t('general'),
      'my profile': t('myProfile'),
      company_info: t('company_info'),
      notifications: t('notifications'),
      transport_providers: t('transport_providers'),
      products: t('products'),
      sensors: t('sensors'),
      credentials: t('credentials'),
      freight_markup_rates: t('freight_markup_rates'),
      locations: t('locations'),
      teams: t('teams'),
      users: t('users'),
      roles_permissions: t('roles_permissions'),
      alert_customization: t('alert_customization'),
      ai_workflow: t('aiWorkflow'),
      gxp_reports: t('gxp_reports'),
      ai_agents: t('ai_agents'),
      ai_controls: t('ai_controls'),
    };

    return labelMap[normalizedLabel] || itemLabel;
  };

  const isModuleMenuItem = (item: { route?: string }): boolean => {
    const normalizedRoute = item?.route?.toLowerCase();

    return normalizedRoute === '/settings/gxp-reports';
  };

  // Loop through submenus
  subMenus?.forEach((item) => {
    const routeKey = item?.route; // like 'profile', 'couriers', etc.
    const label = getTranslatedLabel(item?.label || item?.name || '');
    const name = item?.name?.toLowerCase(); // case-insensitivity

    if (name === 'general') {
      mySettingsChildren.push({
        key: routeKey,
        label: label,
        type: 'item',
      });
    } else if (isModuleMenuItem(item)) {
      modulesChildren.push({
        key: routeKey,
        label: label,
        type: 'item',
      });
    } else {
      companySettingsChildren.push({
        key: routeKey,
        label: label,
        type: 'item',
      });
    }
  });

  // Now build the final items array
  if (mySettingsChildren.length > 0) {
    items.push({
      key: 'mySettings',
      type: 'group',
      label: t('mySettings'),
      children: mySettingsChildren,
    });
  }

  if (companySettingsChildren.length > 0) {
    items.push({
      key: 'companySettings',
      type: 'group',
      label: t('companySettings'),
      children: companySettingsChildren,
    });
  }

  if (modulesChildren.length > 0) {
    items.push({
      key: 'modules',
      type: 'group',
      label: 'Modules',
      children: modulesChildren,
    });
  }

  const navigate = useNavigate();
  const location = useLocation();

  const handleClick: MenuProps['onClick'] = (e) => {
    // Normalize route by ensuring it starts with /
    const route = e?.key?.startsWith('/') ? e?.key : `/${e?.key}`;
    navigate(route);
  };

  const handleBackClick = () => {
    if (backUrl) {
      navigate(backUrl);
    }
  };

  const getSelectedKeys = (): string[] => {
    const currentPath = location?.pathname.split('/')[2];
    const activeItem = items
      .flatMap((group) => group.children)
      .find((item) => item?.key.split('/')[2] === currentPath);
    return activeItem ? [activeItem?.key] : [];
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991); // Mobile size threshold (you can adjust)
    };

    handleResize(); // Check size on mount
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Flatten the items for mobile view
  const mobileMenuItems: MenuChild[] = items
    .flatMap((group) => group.children)
    .map((child) => ({
      ...child,
      label: child.label, // Use only the labels directly
    }));

  return (
    <>
      {/* Only render the desktop menu if not on mobile */}
      {!isMobile && (
        <div className='custom-settings-menu-container'>
          {isUpdateForm ? (
            <Flex
              align='center'
              onClick={handleBackClick}
              style={{ cursor: 'pointer', minHeight: '38px' }}
            >
              <img src={BackArrow} alt='' />
            </Flex>
          ) : (
            <Menu
              style={{
                width: 186,
              }}
              mode='inline'
              // @ts-expect-error - we should be using antd Menu types (see above)
              items={items}
              onClick={handleClick}
              selectedKeys={getSelectedKeys()}
              className='custom-settings-menu'
            />
          )}
        </div>
      )}

      {/* Only render the mobile menu if on mobile */}
      {isMobile && (
        <>
          {isUpdateForm ? (
            <Flex
              align='center'
              onClick={handleBackClick}
              style={{ cursor: 'pointer', minHeight: '38px' }}
            >
              <img src={BackArrow} alt='' />
            </Flex>
          ) : (
            <Menu
              mode='inline'
              // @ts-expect-error - we should be using antd Menu types (see above)
              items={mobileMenuItems}
              onClick={handleClick}
              selectedKeys={getSelectedKeys()}
              className='custom-settings-menu-mobile flex'
            />
          )}
        </>
      )}
    </>
  );
};

export default SettingsSidebar;
